import { useNavigate } from 'react-router-dom';
import { X, Crown, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

const UpgradeModal = ({ isOpen, onClose, limitType }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const getLimitMessage = () => {
    switch (limitType) {
      case 'connections':
        return {
          title: 'Upgrade to Premium',
          message: 'All users have unlimited connections!',
          benefits: [
            'Unlimited messaging',
            'AI match recommendations',
            'Verified badge'
          ]
        };
      case 'messages':
        return {
          title: 'Daily Message Limit Reached',
          message: 'Free users can send up to 5 messages per day.',
          benefits: [
            'Unlimited messaging',
            'Send messages anytime',
            'No daily restrictions'
          ]
        };
      case 'visibility':
        return {
          title: 'Limited Visibility',
          message: 'Free users can only see people from their country.',
          benefits: [
            'Browse users worldwide',
            'Discover people from any country',
            'Expanded networking opportunities'
          ]
        };
      default:
        return {
          title: 'Upgrade to Premium',
          message: 'Unlock all premium features.',
          benefits: [
            'Unlimited connections',
            'Unlimited messaging',
            'Browse users worldwide'
          ]
        };
    }
  };

  const { title, message, benefits } = getLimitMessage();

  const handleUpgrade = () => {
    navigate('/subscription');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-gold to-teal p-8 text-center">
          <Crown className="w-16 h-16 text-white mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">{title}</h2>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 text-center mb-6">{message}</p>

          {/* Benefits */}
          <div className="space-y-3 mb-6">
            <p className="font-semibold text-gray-800">Premium Benefits:</p>
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-3">
                <Sparkles className="w-5 h-5 text-gold flex-shrink-0" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>

          {/* Pricing */}
          <div className="bg-gradient-to-r from-teal-50 to-gold-50 rounded-2xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Starting at</p>
                <p className="text-2xl font-bold text-black">$5.00<span className="text-sm text-gray-600">/month</span></p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Or save 67%</p>
                <p className="text-lg font-bold text-teal">$20.00<span className="text-sm text-gray-600">/year</span></p>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleUpgrade}
              className="w-full py-4 rounded-xl text-base font-semibold bg-white text-black border-2 border-teal hover:bg-teal hover:text-white"
            >
              <Crown className="w-5 h-5 mr-2" />
              Upgrade to Premium
            </Button>
            <Button
              onClick={onClose}
              variant="ghost"
              className="w-full py-3 rounded-xl text-gray-600 hover:bg-gray-100"
            >
              Maybe Later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
